import type { TFunction } from 'i18next'

import type { UnitKind } from '@/types/domain'

/* What moves on a leg, kind by kind (S-18b) — the client's half of a
 * vocabulary the server owns (`casefile.UnitKind`, 0078's
 * `case_leg_units_kind_is_known` CHECK).
 *
 * The wire carries a CODE — 'truck', 'container', 'awb' — and every label is
 * composed here in the reader's language, for `lib/fileKinds.ts`'s reason: a
 * translated string is not a grouping key. The leg page draws one card per kind
 * in `UNIT_KINDS` order, the carrier-payment sheet groups its rows in the same
 * order, and the money surfaces name a unit by `unitLabel` — «Контейнер
 * MSKU1234565», «Фура 01KZ123ABC» — where they used to print a bare plate.
 *
 * Shared by `features/cases` and `features/finance`, which is why it is here
 * and not beside the leg page's own `legs/unitKinds.ts` (the mode defaults and
 * the per-kind columns, which only the leg page reads).
 */

/** Every kind, in the order the leg page's cards and the payment sheet's
 *  groups list them («Фуры», «Контейнеры», «Вагоны», «Авианакладные»,
 *  «Коносаменты», «Другое»). */
export const UNIT_KINDS = ['truck', 'container', 'wagon', 'awb', 'bl', 'other'] as const satisfies readonly UnitKind[]

/** Narrows a kind off the wire, falling back to `other` rather than throwing:
 *  a leg page taken down by a deploy that added a kind is the reverse of what
 *  a fallback is for. */
export function toUnitKind(raw: string | undefined): UnitKind {
  return UNIT_KINDS.find((kind) => kind === raw) ?? 'other'
}

/** A unit as the SERVER matches it (`casefile.unitKey`, 0078's
 *  `case_leg_units_one_number_per_kind`): a truck's plate folded for case and
 *  surrounding spaces (today's plate rule), every other kind's number exact
 *  after trimming (the container rule — `MSKU1234565` and `msku1234565` are two
 *  boxes). The kind is part of the key, so a container and a B/L sharing a
 *  number are two units with two rates. Every map from a write's answer back to
 *  the rows has to fold exactly this way. */
export function unitKey(kind: UnitKind, number: string): string {
  const trimmed = number.trim()
  return `${kind}:${kind === 'truck' ? trimmed.toUpperCase() : trimmed}`
}

type Named = { kind: UnitKind; number: string }

/** «Контейнер MSKU1234565» — a unit named by its kind and number, where a
 *  money surface used to print the bare plate (AC #16). The key shape lives
 *  here once; the cast is the price of an interpolated key, and
 *  `legUnits.test.ts` pins every kind to a label in both locales. */
export function unitLabel(t: TFunction, unit: Named): string {
  return t(`cases.legs.units.label.${unit.kind}` as 'cases.legs.units.label.truck', {
    number: unit.number.trim(),
  })
}

/** The same unit as the OBJECT of an action — «Удалить фуру 01KZ», «Оплатить
 *  за контейнер MSKU…»: Russian declines the noun (фура → фуру), so the
 *  accusative is its own catalogue entry rather than a lowercased label. */
export function unitObject(t: TFunction, unit: Named): string {
  return t(`cases.legs.units.object.${unit.kind}` as 'cases.legs.units.object.truck', {
    number: unit.number.trim(),
  })
}

/** The card and group title of a kind — «Контейнеры». */
export function unitKindTitle(t: TFunction, kind: UnitKind): string {
  return t(`cases.legs.units.title.${kind}` as 'cases.legs.units.title.truck')
}
